import { clearOfflineCache } from '@/lib/offline-api-layer';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId?: string;
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /**
   * Login: store access token and user info.
   * M-005 SECURITY FIX: Refresh token is NO LONGER stored in localStorage.
   * It lives in an httpOnly cookie set by the backend — invisible to JS.
   */
  login: (accessToken: string, user: User) => void;
  logout: () => void;
  setAccessToken: (accessToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,

      login: (accessToken, user) =>
        set({
          accessToken,
          user,
          isAuthenticated: true,
        }),

      logout: () => {
        // Clear auth state immediately so UI redirects to login
        set({
          accessToken: null,
          user: null,
          isAuthenticated: false,
        });
        // M-014 fix: Clear offline cache with retry — fire-and-forget but with error logging.
        // clearOfflineCache is best-effort; if it fails, stale data may remain in IndexedDB.
        // The cache is tenant-scoped so cross-tenant reads are prevented by key isolation.
        clearOfflineCache().catch((err: unknown) => {
          console.error('[auth-store] Failed to clear offline cache on logout:', err);
        });
        // B-H5 FIX (code review 2026-04-16): Story 107-10 AC-4 teljesítve.
        // Minden `kgc-*` localStorage kulcsot törlünk (nem csak sidebar-t),
        // hogy a Zsuzsi widget config + más tenant-szintű state ne szivárogjon
        // a következő bejelentkezésbe. A `kgc-auth` kulcsot a zustand persist
        // middleware maga takarítja a state reset után.
        try {
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith('kgc-')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch {
          // Best-effort cleanup
        }
      },

      setAccessToken: accessToken =>
        set({
          accessToken,
        }),
    }),
    {
      name: 'kgc-auth',
      // M-005 SECURITY FIX: Refresh token is now in httpOnly cookie — not in localStorage.
      // Only accessToken (short-lived) and user info are persisted locally.
      partialize: state => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
